// @flow
import React, { type Node } from 'react';

import { ID_LAYER_HEADER__SHOPPING__COOKIE_PREFERENCES } from 'Components/utils/identifiers/constants';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { Button } from 'Components/frameworks/button/Button.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Layer } from 'Components/frameworks/layer/Layer.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { TextLink } from 'Components/frameworks/text-link/TextLink.jsx';

import { PreferencesItemSwitch } from './PreferencesItemSwitch.jsx';
import { COOKIES_ARRAY } from './constants';

type Props = {
  approveAriaLabel: string,
  back: string,
  backAriaLabel: string,
  buttonLabel: string,
  closeLabel?: string,
  closeLayer: () => *,
  items: {},
  link: { url: string, label: string },
  onChange: (index: number) => (name: string, checked: boolean) => void,
  onValidatePreferences: () => *,
  personnalCookiesState: { [string]: boolean },
  text: string,
  title: string,
};

export const CookiePreferencesLayer = ({
  approveAriaLabel,
  back,
  backAriaLabel,
  buttonLabel,
  closeLabel,
  closeLayer,
  items,
  link,
  onChange,
  onValidatePreferences,
  personnalCookiesState,
  text,
  title,
}: Props): Node => (
  <Layer
    ariaLabel={title}
    ariaLabelledby={ID_LAYER_HEADER__SHOPPING__COOKIE_PREFERENCES}
    bgColor={COLORS.WHITE}
    buttonLeft={{
      ariaHidden: true,
      iconName: ICONS.LEFT_ARROW,
      label: backAriaLabel,
      onClick: closeLayer,
      tabIndex: -1,
    }}
    closeLabel={closeLabel}
    enterAnimation="slideInBottom"
    hasAnimateEnter
    hasContainer
    hasContainerHeader
    onClose={closeLayer}
  >
    <div className="py-5">
      <div className="text-center">
        <Icon className="mb-5" color={COLORS.BLUE} isNotSquare name={ICONS.CLUBMED} width="10rem" />
      </div>
      {!!title && (
        <h3
          className="reset-heading font-bold font-sans leading-tight mb-1 text-gray-darker text-md sm:text-lg"
          id={ID_LAYER_HEADER__SHOPPING__COOKIE_PREFERENCES}
        >
          {title}
        </h3>
      )}
      <Text
        className="font-sans leading-normal mb-1 text-gray-darker text-sm sm:text-base"
        text={text}
      />
      {!!link.label && (
        <p className="font-bold font-sans leading-normal mb-1 mt-0 text-gray-darker text-sm sm:text-base">
          <DynamicLink className="no-underline text-gray-darker" target="_blank" url={link.url}>
            {link.label}
          </DynamicLink>
        </p>
      )}
      {Object.keys(items).map((key, i) => (
        <PreferencesItemSwitch
          index={i}
          isChecked={i === 0 || personnalCookiesState[COOKIES_ARRAY[i - 1]]}
          key={key}
          label={items[key].label}
          name={`CookiePreferences-${key}`}
          onChange={onChange}
          title={items[key].title}
        />
      ))}
      <div className="flex flex flex-col sm:flex-row-reverse sm:items-center sm:justify-between mt-4">
        <Button ariaLabel={approveAriaLabel} className="sm:w-1/6" onClick={onValidatePreferences}>
          {buttonLabel}
        </Button>
        <TextLink
          ariaHidden
          className="block justify-center mt-2 sm:mt-0"
          iconLeftName={ICONS.BOLDER_ARROW_FORTH_LEFT}
          iconRightName={null}
          isUppercase
          label={back}
          onClick={closeLayer}
          tabIndex={-1}
        />
      </div>
    </div>
  </Layer>
);
